import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GREETING = "Hallo und herzlich willkommen beim Haarstudio Nes! Ich beantworte Ihnen gerne Fragen zu Preisen, Öffnungszeiten und Leistungen.";

function getSessionId() {
  let id = localStorage.getItem("nes-chat-session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("nes-chat-session", id);
  }
  return id;
}

export function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  if (pathname === "/anfragen") return null;

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.delta };
                return copy;
              });
            } else if (parsed.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: parsed.error };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Entschuldigung, gerade gab es ein technisches Problem. Rufen Sie uns gerne an: 02238 43 770" };
        return copy;
      });
    }
    setBusy(false);
  }

  return (
    <>
      {!open && (
        <button
          data-testid="chat-open-btn"
          onClick={() => setOpen(true)}
          aria-label="Chat öffnen"
          className="chat-float fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 w-11 h-11 rounded-full bg-[#B08D57] hover:bg-[#967442] text-[#FBF8F3] shadow-md flex items-center justify-center transition-colors duration-300"
        >
          <MessageCircle size={19} />
        </button>
      )}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm h-[480px] max-h-[70vh] bg-[#FBF8F3] border border-[#24211D]/15 shadow-xl flex flex-col"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#24211D]/10 bg-[#FFFFFF]">
            <div>
              <p className="text-sm font-sans uppercase tracking-[0.15em] text-[#24211D]">Haarstudio Nes</p>
              <p className="text-[11px] font-sans text-[#24211D]/60">Fragen? Ich helfe gerne.</p>
            </div>
            <button data-testid="chat-close-btn" onClick={() => setOpen(false)} aria-label="Chat schließen" className="text-[#24211D]/60 hover:text-[#24211D] transition-colors">
              <X size={20} />
            </button>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-message-${m.role}-${i}`}
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#B08D57] text-[#FBF8F3]"
                      : "bg-[#FFFFFF] border border-[#24211D]/10 text-[#24211D]/90"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#24211D]/10 p-3 bg-[#FFFFFF] flex gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ihre Frage …"
              maxLength={1000}
              className="flex-1 bg-transparent text-sm px-3 py-2 outline-none text-[#24211D] placeholder:text-[#24211D]/40"
            />
            <button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={busy || !input.trim()}
              aria-label="Nachricht senden"
              className="w-10 h-10 bg-[#B08D57] hover:bg-[#967442] disabled:opacity-40 text-[#FBF8F3] flex items-center justify-center transition-colors duration-300"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
